import { Component, ElementRef, EventEmitter, HostBinding, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import * as momentImport from "moment-timezone";
import { Moment } from "moment-timezone";
import { CalendarArray, DateSelectionEvent, MomentRange, SelectMode, SimpleDate } from "@spscommerce/ds-shared";

const moment = momentImport as any; // required for rollup to work without a TypeScript error

export function newWeekArray(): SimpleDate[] {
    return new Array(7);
}

@Component({
    selector: "sps-datepicker-calendar",
    templateUrl: "./sps-datepicker-calendar.component.html",
})
export class SpsDatepickerCalendarComponent implements OnChanges {
    @HostBinding("class.sps-datepicker__calendar") readonly _spsDatepickerCalendarClass = true;

    @Input() minDateMoment: Moment;
    @Input() maxDateMoment: Moment;
    @Input() keyboardFocusDay: Moment;

    @Input() view: Moment | MomentRange;
    @Output() viewChange = new EventEmitter<Moment | MomentRange>();

    @Input() value: Moment | MomentRange;
    @Output() dateSelected = new EventEmitter<DateSelectionEvent>();

    selectMode = SelectMode.START;
    weekdayNames: ReadonlyArray<string>;
    months: CalendarArray[];
    mouseoverRange: MomentRange;

    isRange(x: Moment | MomentRange): x is MomentRange {
        return x && !moment.isMoment(x);
    }

    get viewStart() {
        return this.isRange(this.view)
            ? (this.view ? this.view.start : null)
            : this.view;
    }

    get viewEnd() {
        return this.isRange(this.view)
            ? (this.view ? this.view.end : null)
            : this.view;
    }

    constructor(private elementRef: ElementRef) {
        this.weekdayNames = Object.freeze(moment.weekdaysShort());
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.view) {
            this.generateMonths();
        }
    }

    focus() {
        this.elementRef.nativeElement.focus();
    }

    dateIsDisabled(date: SimpleDate | Moment): boolean {
        return date &&
            (this.minDateMoment && this.minDateMoment.isAfter(date, "day")) ||
            (this.maxDateMoment && this.maxDateMoment.isBefore(date, "day"));
    }

    dateIsFocused(date: SimpleDate | Moment): boolean {
        return date && this.keyboardFocusDay && this.keyboardFocusDay.isSame(date, "day");
    }

    dateIsSelected(date: SimpleDate | Moment): boolean {
        return date &&
            this.value && (
                this.isRange(this.value)
                ? (
                    (this.value.start && this.value.start.isSame(date, "day")) ||
                    (this.value.end && this.value.end.isSame(date, "day")) ||
                    (
                        this.value.start &&
                        this.value.end &&
                        this.value.start.isBefore(date, "day") &&
                        this.value.end.isAfter(date, "day")
                    )
                )
                : (
                    this.value.isValid() &&
                    this.value.isSame(date, "day")
                )
            );
    }

    dateIsSelectionStart(date: SimpleDate | Moment): boolean {
        return this.isRange(this.value) &&
            date &&
            this.value &&
            this.value.start && this.value.start.isSame(date, "day") &&
            this.value.end && !this.value.end.isSame(date, "day");
    }

    dateIsSelectionMiddle(date: SimpleDate | Moment): boolean {
        return this.isRange(this.value) &&
            date &&
            this.value &&
            this.value.start && this.value.start.isBefore(date, "day") &&
            this.value.end && this.value.end.isAfter(date, "day");
    }

    dateIsSelectionEnd(date: SimpleDate | Moment): boolean {
        return this.isRange(this.value) &&
            date &&
            this.value &&
            this.value.start && !this.value.start.isSame(date, "day") &&
            this.value.end && this.value.end.isSame(date, "day");
    }

    dateIsWithinPendingSelection(date: SimpleDate | Moment): boolean {
        return this.isRange(this.view) &&
            date &&
            this.mouseoverRange &&
            this.mouseoverRange.start.isBefore(date, "day") &&
            this.mouseoverRange.end.isAfter(date, "day");
    }

    setMouseoverRange(pendingSelectionEndDate: Moment) {
        if (this.isRange(this.value) && this.value.start && this.value.end === null) {
            this.mouseoverRange = {
                start: this.value.start.clone(),
                end: pendingSelectionEndDate
            };
        }
    }

    clearMouseoverRange(event?: MouseEvent) {
        if (!event || (event.target instanceof HTMLElement && event.target.tagName !== "TD")) {
            delete this.mouseoverRange;
        }
    }

    selectDate(date: SimpleDate | Moment) {
        if (date && !this.dateIsDisabled(date)) {
            const mode = this.selectMode;
            if (this.isRange(this.view)) {
                const mdate: Moment = moment.isMoment(date) ? date : moment(date);
                const start = this.value && (this.value as MomentRange).start;
                const end = this.value && (this.value as MomentRange).end;
                const simpleDate: SimpleDate = { year: mdate.year(), month: mdate.month(), date: mdate.date() };
                date = mdate.clone();
                if (this.selectMode === SelectMode.START) {
                    this.value = mdate;
                    this.selectMode = SelectMode.END;
                } else if (!start || date.isBefore(start)) {
                    this.value = {
                        start: start ? start.set(simpleDate) : date,
                        end: null
                    };
                } else {
                    this.value = {
                        start,
                        end: (end ? end.set(simpleDate) : date).endOf("day")
                    };

                    this.selectMode = SelectMode.START;
                    this.clearMouseoverRange();
                }
            } else {
                this.value = this.value
                    ? (
                        moment.isMoment(date)
                            ? (date as Moment).clone()
                            : (this.value as Moment).clone().set((date as SimpleDate))
                    )
                    : moment(date);
            }

            this.dateSelected.emit({
                value: this.value,
                mode,
            });
        }
    }

    onCalendarClick(event: MouseEvent) {
        if (event && event.target) {
            try {
                const date = JSON.parse((<HTMLElement>event.target).getAttribute("data-date"));
                this.selectDate(moment(date));
            } catch (ignore) { }
        }
    }

    onCalendarMouseover(event: MouseEvent) {
        if (this.isRange(this.view)) {
            try {
                const date = JSON.parse((<HTMLElement>event.target).getAttribute("data-date"));
                this.setMouseoverRange(moment(date));
            } catch (ignore) { }
        }
    }

    generateMonth(date: Moment): CalendarArray {
        const baseDate: Partial<SimpleDate> = {
            year: date.year(),
            month: date.month()
        };

        const weekdayArrayIndex = date.weekday();
        const daysInMonth = date.daysInMonth();
        const weeksInMonth = Math.ceil((daysInMonth + weekdayArrayIndex) / 7);

        const weeks = (new Array(weeksInMonth)).fill(true).map(newWeekArray);

        for (let dateIndex = 0; dateIndex < daysInMonth; dateIndex++) {
            const offsetDay = dateIndex + weekdayArrayIndex;
            const week = weeks[Math.floor(offsetDay / 7)];
            const dayOfWeekIndex = offsetDay % 7;

            week[dayOfWeekIndex] = <SimpleDate>Object.freeze(
                Object.assign({ date: dateIndex + 1 }, baseDate)
            );

            if (dayOfWeekIndex === 6) {
                Object.freeze(week);
            }
        }

        return Object.freeze(weeks);
    }

    generateMonths() {
        const startMonth = this.generateMonth(this.viewStart);
        this.months = this.isRange(this.view)
            ? [startMonth, this.generateMonth(this.viewEnd)]
            : [startMonth];
    }

    viewPreviousMonth() {
        if (!this.minDateMoment || this.viewStart.isAfter(this.minDateMoment, "month")) {
            this.viewStart.subtract(1, "month");
            if (this.isRange(this.view)) {
                this.viewEnd.subtract(1, "month");
            }
            this.viewChange.emit(this.view);
            this.generateMonths();
        }
    }

    viewNextMonth() {
        if (!this.maxDateMoment || this.viewEnd.isBefore(this.maxDateMoment, "month")) {
            this.viewStart.add(1, "month");
            if (this.isRange(this.view)) {
                this.viewEnd.add(1, "month");
            }
            this.viewChange.emit(this.view);
            this.generateMonths();
        }
    }

    onKeyDown(event: KeyboardEvent) {
        switch (event.key) {
            case "Up":
            case "Down":
            case "Left":
            case "Right":
            case "ArrowUp":
            case "ArrowDown":
            case "ArrowLeft":
            case "ArrowRight":
                this.setMouseoverRange(this.keyboardFocusDay);
                break;

            case "Esc":
            case "Escape":
                this.clearMouseoverRange();
                if (this.selectMode === SelectMode.END) {
                    this.value = undefined;
                    this.selectMode = SelectMode.START;
                }
                break;

            case "Enter":
                if (this.keyboardFocusDay) {
                    this.selectDate(this.keyboardFocusDay);
                }
                break;
        }

        if (this.keyboardFocusDay) {
            if (this.keyboardFocusDay.isBefore(this.viewStart, "month")) {
                this.viewPreviousMonth();
            } else if (this.keyboardFocusDay.isAfter(this.viewEnd, "month")) {
                this.viewNextMonth();
            }
        }
    }
}
